import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export enum ReportTypes {
  VIEWS = 'views',
  UNIQUE_VIEWS = 'uniqueViews',
  ENGAGEMENT = 'engagements',
  CTA = 'clicks',
  OPENED = 'opened-emails',
  DELIVERED = 'delivered-emails',
  DEFERRED = 'deferred-emails',
  SPAM = 'spam-emails',
  BOUNCED = 'bounced-emails',
  DROPPED = 'dropped-emails',
  UNSUBSCRIBED = 'unsubscribed-emails',
  RECORDED = 'videos',
  SENT = 'sent-emails',
  SENT_UNIQUE = 'sent-unique',
  VIDEO_ATTRIBUTES = 'video-attributes',
  AVERAGE_VIDEO_DURATION = 'average-video-duration',
  LAST_LOGIN = 'lastLogin',
  // CDS report types
  REPAIR_ORDER = 'repair-orders',
  TOTAL_RO_ITEMS = 'total-ro-items',
  REQUIRED_RO_ITEMS = 'required-ro-items',
  OPTIONAL_RO_ITEMS = 'optional-ro-items',
  TOTAL_AMOUNT = 'total-amount',
  AMOUNT_APPROVED = 'amount-approved',
  CLOSE_RATIO = 'close-ratio',
  TIME_WATCHED = 'time-watched',
  WATCH_RATE = 'watch-rate',
  DEPARTMENT = 'dept',
  USER_COUNT = 'user-count',
  TOTAL_SEATS = 'total-seats',
}

export enum REPORT_GROUP_TYPE {
  POPULAR = 'popular',
  COVIDEO = 'covideo',
  SERVICE = 'service',
}

export const reportTypes: { [key: string]: string } = Object.assign(
  {},
  ReportTypes
);
export const reportFieldType = {
  COUNT: 'cnt',
  AVERAGE: 'average',
  MAX: 'max',
} as const;

export type ReportFieldType =
  (typeof reportFieldType)[keyof typeof reportFieldType];

export type ReportConfig = {
  label: string;
  url?: string;
  field?: ReportFieldType; // ← single value like 'cnt' | 'average' | 'max'
  isPercentage?: boolean;
  isTime?: boolean;
  allowDecimals?: boolean;
  hideWidget?: boolean;
  hideInMyStats?: boolean;
  isAutomotive?: boolean;
  columnType?: string;
  tableFields: { value: string; label: string }[];
  description: string;
  group: REPORT_GROUP_TYPE;
};

export const useReportData = () => {
  const { t, i18n } = useTranslation();
  const reportData = useMemo(
    () => ({
      [reportTypes.RECORDED]: {
        label: t('Recorded Videos'),
        url: 'metrics/reports/videos',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'title', label: t('Video Title') },
          { value: 'recordedAt', label: t('Date Recorded') },
        ],
        description: t('Number of personalized videos recorded by staff'),
        group: REPORT_GROUP_TYPE.POPULAR,
      },
      [reportTypes.VIEWS]: {
        label: t('Total Views'),
        url: 'metrics/reports/views',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'title', label: t('Video Title') },
          { value: 'contactName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Viewed') },
          { value: 'watched', label: t('Watched %') },
        ],
        description: t('Cumulative number of views across all videos'),
        group: REPORT_GROUP_TYPE.POPULAR,
      },
      [reportTypes.CTA]: {
        label: t('CTA Clicks'),
        url: 'metrics/reports/clicks',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'date', label: t('Click Date') },
          { value: 'email', label: t('Email Address') },
          { value: 'cta', label: t('CTA') },
          { value: 'linkSet', label: t('CTA Set') },
          { value: 'template', label: t('Landing Page') },
        ],
        description: t(
          'Count of clicks on call-to-action buttons on your landing page'
        ),
        group: REPORT_GROUP_TYPE.POPULAR,
      },
      [reportTypes.ENGAGEMENT]: {
        label: t('Engagement Rate'),
        url: 'metrics/reports/engagements',
        field: reportFieldType.AVERAGE,
        isPercentage: true,
        tableFields: [
          { value: 'contactName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'title', label: t('Video Title') },
          { value: 'date', label: t('Date Viewed') },
          { value: 'watched', label: t('Watched %') },
        ],
        description: t(
          "The percentage of each video's total duration that has been viewed. If applied to more than one video, it is averaged across all videos within the selected time period."
        ),
        group: REPORT_GROUP_TYPE.POPULAR,
      },
      [reportTypes.OPENED]: {
        label: t('Opened'),
        url: 'metrics/reports/opened-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'contactFullName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Sent') },
          { value: 'title', label: t('Video Title') },
          { value: 'status', label: t('View Status') },
          { value: 'lastViewDate', label: t('Last Viewed') },
        ],
        description: t('Number of times recipients opened your videos'),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.DELIVERED]: {
        label: t('Delivered'),
        url: 'metrics/reports/delivered-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'contactFullName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Sent') },
          { value: 'title', label: t('Video Title') },
          { value: 'status', label: t('View Status') },
          { value: 'lastViewDate', label: t('Last Viewed') },
        ],
        description: t("Videos successfully delivered to recipients' inboxes"),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.DEFERRED]: {
        label: t('Deferred'),
        url: 'metrics/reports/deferred-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Deferred') },
        ],
        description: t(
          'The video sent, but the recipient’s mail server delayed delivery'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.SPAM]: {
        label: t('Spam'),
        url: 'metrics/reports/spam-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date') },
        ],
        description: t('Videos marked as spam by recipient email providers'),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.BOUNCED]: {
        label: t('Bounced'),
        url: 'metrics/reports/bounced-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Bounced') },
        ],
        description: t(
          'The videos that failed to reach recipients due to undeliverable addresses'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.DROPPED]: {
        label: t('Dropped'),
        url: 'metrics/reports/dropped-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Dropped') },
        ],
        description: t(
          'Videos that failed to reach recipients due to technical issues or restrictions'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.UNSUBSCRIBED]: {
        label: t('Unsubscribed'),
        url: 'metrics/reports/unsubscribed-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'date', label: t('Date Unsubscribed') },
        ],
        description: t(
          'Count of recipients who opted out of receiving further videos'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.SENT]: {
        label: t('Sent'),
        url: 'metrics/reports/sent-emails',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'contactFullName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'phone', label: t('Phone') },
          { value: 'date', label: t('Date Sent') },
          { value: 'title', label: t('Video Title') },
          { value: 'status', label: t('View Status') },
          { value: 'lastViewDate', label: t('Last Viewed') },
        ],
        description: t('Total amount of videos sent to recipients'),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.VIDEO_ATTRIBUTES]: {
        label: t('Video Attributes'),
        url: 'metrics/reports/video-attributes',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'title', label: t('Attribute Name') },
          { value: 'attributeType', label: t('Type') },
          { value: 'valueCount', label: t('Values') },
          { value: 'videoCount', label: t('Total Videos') },
        ],
        description: t(
          'Custom video grouping such as type and vehicle information'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.AVERAGE_VIDEO_DURATION]: {
        label: t('Avg. Video Duration'),
        url: 'metrics/reports/average-video-duration',
        field: reportFieldType.AVERAGE,
        isPercentage: false,
        isTime: true,
        tableFields: [
          { value: 'userFullName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'duration', label: t('Duration') },
        ],
        description: t('Average length of videos in minutes and seconds'),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.LAST_LOGIN]: {
        label: t('Last Login'),
        hideWidget: true,
        tableFields: [],
        description: t('Timestamp of when a user most recently logged in'),
        group: REPORT_GROUP_TYPE.COVIDEO,
        columnType: 'string',
        hideInMyStats: true,
      },
      [reportTypes.UNIQUE_VIEWS]: {
        label: t('Unique Views'),
        description: t('Cumulative number of unique views across all videos'),
        group: REPORT_GROUP_TYPE.COVIDEO,
        url: 'metrics/reports/uniqueViews',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'title', label: t('Video Title') },
          { value: 'recordedAt', label: t('Date Recorded') },
        ],
      },
      [reportTypes.WATCH_RATE]: {
        label: t('Watch Rate'),
        description: t(
          'The percentage of videos viewed after being sent via email, SMS, or quick share within the selected time period.'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
        url: 'metrics/reports/watch-rate',
        field: reportFieldType.AVERAGE,
        isPercentage: true,
        tableFields: [
          { value: 'name', label: t('Name') },
          { value: 'watch-rate', label: t('Watch Rate') },
        ],
      },
      [reportTypes.REPAIR_ORDER]: {
        label: 'No. of ROs with Quotes',
        url: 'metrics/reports/repair-orders',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'date', label: t('Date') },
        ],
        isAutomotive: true,
        description: t('Total amount of repair orders with Quotes'),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.TOTAL_RO_ITEMS]: {
        label: t('Total RO items'),
        url: 'metrics/reports/total-ro-items',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'title', label: t('RO Title') },
          { value: 'date', label: t('Date') },
        ],
        isAutomotive: true,
        description: t('Overall count of items on repair orders'),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.REQUIRED_RO_ITEMS]: {
        label: t('Req. RO items'),
        url: 'metrics/reports/required-ro-items',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'title', label: t('RO Title') },
          { value: 'date', label: t('Date') },
        ],
        isAutomotive: true,
        description: t('Number of items requested on repair orders'),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.OPTIONAL_RO_ITEMS]: {
        label: t('Opt RO items'),
        url: 'metrics/reports/optional-ro-items',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'title', label: t('RO Title') },
          { value: 'date', label: t('Date') },
        ],
        isAutomotive: true,
        description: t(
          'Number of items on repair orders approved by customers'
        ),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.TOTAL_AMOUNT]: {
        label: t('Total amount'),
        url: 'metrics/reports/total-amount',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'title', label: t('RO Title') },
          { value: 'date', label: t('Date') },
          { value: 'price', label: t('Price') },
        ],
        isAutomotive: true,
        description: t('Cumulative monetary value of all repair orders'),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.AMOUNT_APPROVED]: {
        label: t('Amount approved'),
        url: 'metrics/reports/amount-approved',
        field: reportFieldType.COUNT,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'title', label: t('RO Title') },
          { value: 'date', label: t('Date') },
          { value: 'price', label: t('Price') },
        ],
        isAutomotive: true,
        description: t(
          'Total monetary value of repair orders approved by customers'
        ),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.CLOSE_RATIO]: {
        label: t('Close ratio'),
        url: 'metrics/reports/close-ratio',
        field: reportFieldType.MAX,
        isPercentage: false,
        tableFields: [
          { value: 'advisorName', label: t('Advisor name') },
          { value: 'date', label: t('Date') },
          { value: 'ratio', label: t('Close ratio') },
        ],
        allowDecimals: true,
        isAutomotive: true,
        description: t(
          'Ratio of repair orders closed successfully out of total sent repair orders'
        ),
        group: REPORT_GROUP_TYPE.SERVICE,
      },
      [reportTypes.TIME_WATCHED]: {
        label: t('Time Watched'),
        url: 'metrics/reports/time-watched',
        field: reportFieldType.COUNT,
        isPercentage: false,
        isTime: true,
        tableFields: [
          { value: 'contactName', label: t('Name') },
          { value: 'email', label: t('Email Address') },
          { value: 'title', label: t('Video Title') },
          { value: 'date', label: t('Date Viewed') },
          { value: 'time-watched', label: t('Time watched') },
        ],
        description: t(
          'The total number of minutes customers have spent watching videos within the selected time period.'
        ),
        group: REPORT_GROUP_TYPE.COVIDEO,
      },
      [reportTypes.DEPARTMENT]: {
        label: t('Department'),
        hideWidget: true,
        tableFields: [],
        description: t('Include department details inside the report'),
        group: REPORT_GROUP_TYPE.COVIDEO,
        columnType: 'string',
        hideInMyStats: true,
      },
      [reportTypes.USER_COUNT]: {
        label: t('Active User Count'),
        description: t('Total amount of Active Users'),
        group: REPORT_GROUP_TYPE.COVIDEO,
        hideWidget: true,
        tableFields: [],
        hideInMyStats: true,
      },
      [reportTypes.TOTAL_SEATS]: {
        label: t('Total Seats'),
        description: t('Total number of individual user licenses'),
        group: REPORT_GROUP_TYPE.COVIDEO,
        hideWidget: true,
        tableFields: [],
        hideInMyStats: true,
      },
    }),
    [i18n.language, t]
  );

  return { reportData };
};

// not part of reportData since it is added to custom reports only (for now)
export const getSentUniqueReportData = (t: TFunction) => {
  return {
    label: t('Sent Unique'),
    hideWidget: true,
    tableFields: [],
    description: t('Total amount of videos sent to unique recipients'),
    group: REPORT_GROUP_TYPE.POPULAR,
  };
};
